import { useEffect, useMemo, useState } from "react";
import FilterProps from "./FilterProps";
import ValidatedInput from "../../UI/ValidatedInput";
import ValidatedSelect from "../../UI/ValidatedSelect";
import { fieldTypeOperatorMap } from "../../../constants/customFilterDataType";
import ApiErrorMessages from "../../UI/ApiErrorMessages";
import Loader from "../../UI/Loader";
import { operatorLabelMap, UNANSWERED } from "../../../constants/filterOperator";
import { groupingNameMap } from "../../../constants/filterGrouping";
import IconButton from "../../UI/IconButton";
import FormField from "../../../models/serviceModels/FormField";
import { getForm } from "../../../services/formsService";
import * as filterOperators from "../../../constants/filterOperator";
import { ACTIVITY_DATE_ID, BREAK_LINE_ID, CLIENT_ID, DATE_ID, HEADER_ID } from "../../../constants/fieldTypes";
import Toggle from "../../UI/Toggle";
import { AND, OR } from "../../../constants/customFilterType";
import Checkbox from "../../UI/Checkbox";
import { FORM_SELECTION } from "../../../constants/reportStep";
import FormFieldValidationResponse, { defaultValue } from "../../../utils/validation/reportFilters/FormFieldValidateResponse";
import ErrorMessages from "../../UI/ErrorMessages";

const FormFieldFilter = (props: FilterProps) => {
    const validation = props.validation as FormFieldValidationResponse || defaultValue();

    const [fields, setFields] = useState<FormField[]>([]);
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState<any>();

    const getParsedValue = () => {
        const defaultValue = {
            field: '',
            value: '',
            from: '',
            to: '',
            fieldTypeId: ''
        };

        if (!props.value || props.value.indexOf('{') === -1) {
            return defaultValue;
        }

        try {
            const parsed = JSON.parse(props.value);
            return parsed;
        } catch {
            return defaultValue;
        }
    };

    useEffect(() => {
        if (!props.value && props.onValueChange) {
            props.onValueChange(props.id, JSON.stringify(getParsedValue()));
        }
    }, [props.value]);

    useEffect(() => {
        const formSelectionStepValues: { name: string, value: string }[] = props.allStepValues[FORM_SELECTION];
        if (!formSelectionStepValues) {
            console.error('Form selection step not found.  Unable to find which form to load');
            setApiError('Failed to load fields');
            return;
        }

        const formIdValue = formSelectionStepValues.find(v => v.name === 'form_id');
        if (!formIdValue?.value) {
            console.error('Form Id not found. Unable to load form');
            setApiError('Failed to load fields');
            return;
        }

        const versionIdValue = formSelectionStepValues.find(v => v.name === 'version_id');
        if (!versionIdValue?.value) {
            console.error('Version Id not found. Unable to load form');
            setApiError('Failed to load fields');
            return;
        }

        setLoading(true);

        getForm(formIdValue.value, versionIdValue.value).then(f => {
            setFields(f.fields.filter(f => f.fieldTypeId !== BREAK_LINE_ID && f.fieldTypeId !== HEADER_ID && f.fieldTypeId !== CLIENT_ID));
        }).catch(e => {
            console.error(e);
            setApiError(e);
        }).finally(() => {
            setLoading(false);
        });
    }, []);

    useEffect(() => {
        if (props.showEndConjunction) {
            handleOnEndConjunctionChange(props.endConjunction);
        } else {
            handleOnEndConjunctionChange(undefined);
        }
    }, [props.showEndConjunction, props.endConjunction]);

    useEffect(() => {
        if (props.showStartConjunction) {
            handleOnStartConjunctionChange(props.startConjunction);
        } else {
            handleOnStartConjunctionChange(undefined);
        }
    }, [props.showStartConjunction, props.startConjunction]);

    const handleOnStartConjunctionChange = (conjunction?: string) => {
        props.onStartConjunctionChange(props.id, conjunction);
    };

    const handleOnEndConjunctionChange = (conjunction?: string) => {
        props.onEndConjunctionChange(props.id, conjunction);
    };

    const handleOnSelectedChanged = (selected: boolean) => {
        props.onSelectedChange!(props.id, selected);
    };

    const handleOnOperatorChange = (operator: string) => {
        if (operator === UNANSWERED) {
            const parsedValue = getParsedValue();
            props.onValueChange!(props.id, JSON.stringify({
                ...parsedValue,
                field: parsedValue.field,
                value: ''
            }));
        }
        props.onOperatorChange!(props.id, operator);
    };

    const handleOnFieldChange = (value: string) => {
        const existingValue = getParsedValue();
        const matchingField = fields.find(f => f.name === value);
        props.onValueChange!(props.id, JSON.stringify({
            ...parsedValue,
            fieldTypeId: matchingField?.fieldTypeId,
            field: value,
            value: existingValue.value || ''
        }));
    };

    const handleOnValueChange = (value: string, property?: string) => {
        const existingValue = getParsedValue();
        if (property) {
            existingValue[property] = value;
        } else {
            existingValue['value'] = value;
        }

        props.onValueChange!(props.id, JSON.stringify(existingValue));
    };

    const handleOnDelete = () => {
        props.onDelete(props.id);
    };

    const getInputs = (field: FormField) => {
        if (!props.operator || !field) {
            return null;
        }

        switch (props.operator) {
            case filterOperators.EQUALS:
            case filterOperators.NOT_EQUALS:
            case filterOperators.GREATER_THAN:
            case filterOperators.GREATER_THAN_OR_EQUAL_TO:
            case filterOperators.LESS_THAN:
            case filterOperators.LESS_THEN_OR_EQUAL_TO:
            case filterOperators.ENDS_WITH:
            case filterOperators.NOT_ENDS_WITH:
            case filterOperators.STARTS_WITH:
            case filterOperators.NOT_STARTS_WITH:
            case filterOperators.CONTAINS:
            case filterOperators.NOT_CONTAINS:
                if (field.options) {
                    return <ValidatedSelect id={`filter-select-${props.id}`}
                        value={parsedValue.value}
                        onChange={handleOnValueChange}
                        label="Value"
                        messages={validation.value}>
                        <option value="">Select One</option>
                        {field.options.map(pv => {
                            return <option key={pv.value} value={pv.value}>{pv.label}</option>
                        })}
                    </ValidatedSelect>;
                }

                if (field.fieldTypeId === DATE_ID || field.fieldTypeId === ACTIVITY_DATE_ID) {
                    return <>
                        <ValidatedInput id={`filter-text-equals-${props.id}`}
                            messages={validation.value}
                            value={parsedValue.value}
                            label="Value"
                            type="date"
                            onChange={handleOnValueChange} />
                    </>;
                }
                return <>
                    <ValidatedInput id={`filter-text-equals-${props.id}`}
                        messages={validation.value}
                        label="Value"
                        value={parsedValue.value}
                        onChange={handleOnValueChange} />
                </>;
            case filterOperators.BETWEEN:
                return <>
                    <ErrorMessages messages={validation.fromAndTo} />
                    <div className="row align-items-center justify-content-between">
                        <div className="column-auto">
                            <ValidatedInput id={`date-filter-from-${props.id}`}
                                messages={validation.from}
                                value={parsedValue.from}
                                label="From"
                                onChange={v => handleOnValueChange(v, 'from')}
                                type="date" />
                        </div>
                        <div className="column-auto margin-bottom-5">
                            and
                        </div>
                        <div className="column-auto">
                            <ValidatedInput id={`date-filter-to-${props.id}`}
                                messages={validation.to}
                                value={parsedValue.to}
                                label="To"
                                onChange={v => handleOnValueChange(v, 'to')}
                                type="date" />
                        </div>
                    </div>
                </>
            case filterOperators.UNANSWERED:
                return null;
            default:
                console.warn('Operator is unknown for date filter', props.operator);
                break;
        }

        return null;
    };


    if (loading) {
        return <Loader />
    }

    const parsedValue = getParsedValue();
    const currentField: FormField = parsedValue.field && fields.find(f => f.name === parsedValue.field);

    return <>
        {props.showStartConjunction && <Toggle id={`conjunction-${props.id}`}
            option1={{ label: 'And', value: AND }}
            option2={{ label: 'Or', value: OR }}
            onChange={handleOnStartConjunctionChange}
            value={props.startConjunction} />}
        <ApiErrorMessages error={apiError} />
        <ErrorMessages messages={validation.default} />
        <div className="row align-items-center justify-content-between">
            <div className="column-auto">
                <Checkbox id={`filter-selected-${props.id}`}
                    checked={props.selected}
                    label="Select to Group"
                    containerClassName="unboxed margin-top-0"
                    onChange={handleOnSelectedChanged} />
            </div>
            <div className="column-auto">
                <IconButton id={`delete-filter-${props.id}`}
                    icon={<i className="ri-delete-bin-line"></i>}
                    onClick={handleOnDelete}
                    className="small flat" />
            </div>
        </div>
        <div className="row padding-3">
            <div className="column-auto text-bold">
                {groupingNameMap[props.grouping]} - Form Fields
            </div>
        </div>
        {!currentField && parsedValue?.field && <div className="row">
            <div className="column">
                <span className="error-message">
                    Field not found. Try recreating filter or contact support.
                </span>
            </div>
        </div>}
        <div className="row align-items-center padding-3 filter-config-row">
            <div className="column-3 filter-field-column">
                <ValidatedSelect id={`filter-field-${props.id}`}
                    value={parsedValue.field}
                    onChange={handleOnFieldChange}
                    messages={validation.field}
                    label="Field">
                    <option value="">Select One</option>
                    {fields.map(f => {
                        return <option key={f.name} value={f.name}>{f.label}</option>
                    })}
                </ValidatedSelect>
            </div>
            <div className="column-3 filter-operator-column">
                <ValidatedSelect id={`filter-operation-${props.id}`}
                    value={props.operator}
                    onChange={handleOnOperatorChange}
                    label="Operator"
                    messages={validation.operator}>
                    <option value="">Select One</option>
                    {currentField && fieldTypeOperatorMap[currentField.fieldTypeId] && fieldTypeOperatorMap[currentField.fieldTypeId].map((m, i) => {
                        return <option key={i} value={m}>{operatorLabelMap[m]}</option>
                    })}
                </ValidatedSelect>
            </div>
            <div className="column-auto filter-value-column">
                {getInputs(currentField)}
            </div>
        </div>
        {props.showEndConjunction && <Toggle id={`conjunction-${props.id}`}
            option1={{ label: 'And', value: AND }}
            option2={{ label: 'Or', value: OR }}
            onChange={handleOnEndConjunctionChange}
            value={props.endConjunction} />}
    </>
};

export default FormFieldFilter;
